/**
 * Rangos espartanos (seccion 9.1).
 * El rango mide progresion individual y es transversal a las categorias
 * (RF-RANGO-05): dos atletas de categorias distintas pueden tener el mismo.
 */

import { RANKS, type RankDefinition } from '@/config/ranks';

/** Rango que corresponde a unos puntos de temporada. */
export function rankForPoints(points: number): RankDefinition {
  const safe = Number.isFinite(points) ? Math.max(0, points) : 0;
  // De mayor a menor: el primero cuyo minimo se alcanza.
  for (let i = RANKS.length - 1; i >= 0; i -= 1) {
    if (safe >= RANKS[i].minPoints) return RANKS[i];
  }
  return RANKS[0];
}

export function rankByLevel(level: number): RankDefinition {
  return RANKS.find((r) => r.level === level) ?? RANKS[0];
}

export interface RankProgress {
  current: RankDefinition;
  next: RankDefinition | null;
  /** Porcentaje 0-100 recorrido dentro del rango actual. */
  percent: number;
  /** Puntos que faltan para el siguiente rango (RF-RANGO-03). */
  pointsToNext: number;
  isMax: boolean;
}

/** Progreso dentro del rango actual, para la barra del perfil (RF-RANGO-03). */
export function rankProgress(points: number): RankProgress {
  const safe = Number.isFinite(points) ? Math.max(0, points) : 0;
  const current = rankForPoints(safe);
  const next = RANKS.find((r) => r.level === current.level + 1) ?? null;

  if (!next || current.maxPoints === null) {
    return { current, next: null, percent: 100, pointsToNext: 0, isMax: true };
  }

  const span = next.minPoints - current.minPoints;
  const done = safe - current.minPoints;
  const percent = span > 0 ? Math.min(100, Math.max(0, (done / span) * 100)) : 0;

  return {
    current,
    next,
    percent,
    pointsToNext: Math.max(0, next.minPoints - safe),
    isMax: false,
  };
}

/**
 * Puntos con los que arranca la temporada siguiente (RF-TEMP-05).
 * Se conserva el porcentaje de arrastre configurado en la temporada, 40 % por
 * defecto; el resto se reinicia y el rango se recalcula sobre esa base, por lo
 * que puede descender (RF-RANGO-04).
 */
export function carryoverPoints(seasonPoints: number, carryoverPct: number): number {
  const pct = Number.isFinite(carryoverPct) ? Math.min(100, Math.max(0, carryoverPct)) : 0;
  const points = Number.isFinite(seasonPoints) ? Math.max(0, seasonPoints) : 0;
  return Math.round(points * (pct / 100));
}

/** true si los puntos nuevos cruzan un umbral de rango (dispara la animacion). */
export function crossedRankUp(previousPoints: number, newPoints: number): boolean {
  return rankForPoints(newPoints).level > rankForPoints(previousPoints).level;
}
